import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Camera, CheckCircle2, ChevronLeft, ChevronRight, FileText, Save, Upload, UserCheck, Users, Video, VideoOff } from 'lucide-react';
import { toast } from 'sonner';

import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { getErrorMessage } from '../lib/utils';
import { filterVisibleEmployees } from '../lib/employeeVisibility';
import { employeeAPI, onboardingAPI, payrollAPI } from '../services/api';

const STEP_ORDER = ['profile', 'kyc', 'family', 'education', 'salary', 'approvals'];

const STEP_META = [
  { id: 'profile', label: 'Profile' },
  { id: 'kyc', label: 'KYC & Photo' },
  { id: 'family', label: 'Family & Nominee' },
  { id: 'education', label: 'Education & Work' },
  { id: 'salary', label: 'Salary & Bank' },
  { id: 'approvals', label: 'Approvals & Docs' },
];

const createFamilyMember = () => ({
  full_name: '',
  relationship: '',
  date_of_birth: '',
  contact_number: '',
  occupation: '',
  dependent: false,
});

const createNominee = () => ({
  full_name: '',
  relationship: '',
  percentage: '',
  contact_number: '',
  address: '',
});

const createEducationRow = (level = '') => ({
  level,
  institution_name: '',
  board_or_university: '',
  specialization: '',
  graduation_year: '',
  score: '',
});

const createWorkHistoryRow = () => ({
  employment_type: 'full_time',
  company_name: '',
  job_title: '',
  location: '',
  start_date: '',
  end_date: '',
  currently_active: false,
  responsibilities: '',
});

const createApprovalRow = (level) => ({
  level,
  manager_id: '',
  leave_approver_id: '',
  expense_approver_id: '',
  other_approver_id: '',
  role_label: `Level ${level}`,
});

const defaultApprovalHierarchy = () => [1, 2, 3, 4].map((level) => createApprovalRow(level));

const toInputValue = (value) => (value === null || value === undefined ? '' : String(value));

const toDatetimeValue = (value) => {
  if (!value) return null;
  return String(value).includes('T') ? value : `${value}T00:00:00`;
};

const managerSelectValue = (value) => value || '__none__';

const normalizeEmployeePayload = (form) => ({
  preferred_name: form.onboarding_profile.preferred_name || null,
  department: form.onboarding_profile.department || null,
  sub_department: form.onboarding_profile.sub_department || null,
  designation: form.onboarding_profile.designation || null,
  location: form.onboarding_profile.location || null,
  employment_type: form.onboarding_profile.employment_type || null,
  work_mode: form.onboarding_profile.work_mode || null,
  grade: form.onboarding_profile.grade || null,
  cost_center: form.onboarding_profile.cost_center || null,
  date_of_joining: toDatetimeValue(form.onboarding_profile.date_of_joining),
  probation_end_date: form.onboarding_profile.probation_end_date || null,
  phone: form.kyc_details.phone || null,
  personal_email: form.kyc_details.personal_email || null,
  address: form.kyc_details.address || null,
  gender: form.kyc_details.gender || null,
  marital_status: form.kyc_details.marital_status || null,
  blood_group: form.kyc_details.blood_group || null,
  nationality: form.kyc_details.nationality || null,
  date_of_birth: form.kyc_details.date_of_birth || null,
  pan_number: form.kyc_details.pan_number || null,
  aadhaar_number: form.kyc_details.aadhaar_number || null,
  uan_number: form.kyc_details.uan_number || null,
  pf_number: form.kyc_details.pf_number || null,
  esi_number: form.kyc_details.esi_number || null,
  bank_name: form.bank_details.bank_name || null,
  bank_account_number: form.bank_details.bank_account_number || null,
  ifsc_code: form.bank_details.ifsc_code || null,
  reporting_manager_id: form.approval_hierarchy[0]?.manager_id || null,
  second_level_manager_id: form.approval_hierarchy[1]?.manager_id || null,
  third_level_manager_id: form.approval_hierarchy[2]?.manager_id || null,
  fourth_level_manager_id: form.approval_hierarchy[3]?.manager_id || null,
});

const buildInitialForm = (employee, wizardData) => ({
  checklist_id: toInputValue(wizardData?.checklist_id),
  hr_assigned: toInputValue(wizardData?.hr_assigned),
  buddy_assigned: toInputValue(wizardData?.buddy_assigned),
  target_days: wizardData?.target_completion_date ? 30 : 30,
  status: wizardData?.status && wizardData.status !== 'not_started' ? wizardData.status : 'pending',
  notes: toInputValue(wizardData?.notes),
  onboarding_profile: {
    preferred_name: toInputValue(wizardData?.onboarding_profile?.preferred_name || employee?.preferred_name),
    employment_type: toInputValue(wizardData?.onboarding_profile?.employment_type || employee?.employment_type),
    work_mode: toInputValue(wizardData?.onboarding_profile?.work_mode || employee?.work_mode),
    grade: toInputValue(wizardData?.onboarding_profile?.grade || employee?.grade),
    cost_center: toInputValue(wizardData?.onboarding_profile?.cost_center || employee?.cost_center),
    department: toInputValue(wizardData?.onboarding_profile?.department || employee?.department),
    sub_department: toInputValue(wizardData?.onboarding_profile?.sub_department || employee?.sub_department),
    designation: toInputValue(wizardData?.onboarding_profile?.designation || employee?.designation),
    location: toInputValue(wizardData?.onboarding_profile?.location || employee?.location),
    date_of_joining: toInputValue(wizardData?.onboarding_profile?.date_of_joining).slice(0, 10),
    probation_end_date: toInputValue(wizardData?.onboarding_profile?.probation_end_date).slice(0, 10),
    notes: toInputValue(wizardData?.onboarding_profile?.notes),
  },
  kyc_details: {
    phone: toInputValue(wizardData?.kyc_details?.phone || employee?.phone),
    personal_email: toInputValue(wizardData?.kyc_details?.personal_email || employee?.personal_email),
    address: toInputValue(wizardData?.kyc_details?.address || employee?.address),
    gender: toInputValue(wizardData?.kyc_details?.gender || employee?.gender),
    marital_status: toInputValue(wizardData?.kyc_details?.marital_status || employee?.marital_status),
    blood_group: toInputValue(wizardData?.kyc_details?.blood_group || employee?.blood_group),
    nationality: toInputValue(wizardData?.kyc_details?.nationality || employee?.nationality),
    date_of_birth: toInputValue(wizardData?.kyc_details?.date_of_birth || employee?.date_of_birth).slice(0, 10),
    pan_number: toInputValue(wizardData?.kyc_details?.pan_number || employee?.pan_number),
    aadhaar_number: toInputValue(wizardData?.kyc_details?.aadhaar_number || employee?.aadhaar_number),
    uan_number: toInputValue(wizardData?.kyc_details?.uan_number || employee?.uan_number),
    pf_number: toInputValue(wizardData?.kyc_details?.pf_number || employee?.pf_number),
    esi_number: toInputValue(wizardData?.kyc_details?.esi_number || employee?.esi_number),
    documents_note: toInputValue(wizardData?.kyc_details?.documents_note),
  },
  family_details:
    Array.isArray(wizardData?.family_details) && wizardData.family_details.length > 0
      ? wizardData.family_details.map((row) => ({ ...createFamilyMember(), ...row, dependent: Boolean(row?.dependent) }))
      : [createFamilyMember()],
  nominee_details:
    Array.isArray(wizardData?.nominee_details) && wizardData.nominee_details.length > 0
      ? wizardData.nominee_details.map((row) => ({ ...createNominee(), ...row }))
      : [createNominee()],
  education_details:
    Array.isArray(wizardData?.education_details) && wizardData.education_details.length > 0
      ? wizardData.education_details.map((row) => ({ ...createEducationRow(), ...row }))
      : [createEducationRow('10th'), createEducationRow('12th'), createEducationRow('Last Degree')],
  work_history_details:
    Array.isArray(wizardData?.work_history_details) && wizardData.work_history_details.length > 0
      ? wizardData.work_history_details.map((row) => ({ ...createWorkHistoryRow(), ...row, currently_active: Boolean(row?.currently_active) }))
      : [createWorkHistoryRow()],
  salary_details: {
    salary_structure_id: toInputValue(wizardData?.salary_details?.salary_structure_id),
    ctc_annual: toInputValue(wizardData?.salary_details?.ctc_annual),
    ctc_monthly: toInputValue(wizardData?.salary_details?.ctc_monthly),
    basic_salary: toInputValue(wizardData?.salary_details?.basic_salary),
    hra: toInputValue(wizardData?.salary_details?.hra),
    gross_salary: toInputValue(wizardData?.salary_details?.gross_salary),
    net_salary: toInputValue(wizardData?.salary_details?.net_salary),
    pf_employee: toInputValue(wizardData?.salary_details?.pf_employee),
    pf_employer: toInputValue(wizardData?.salary_details?.pf_employer),
    esi_employee: toInputValue(wizardData?.salary_details?.esi_employee),
    esi_employer: toInputValue(wizardData?.salary_details?.esi_employer),
    professional_tax: toInputValue(wizardData?.salary_details?.professional_tax),
    other_deductions: toInputValue(wizardData?.salary_details?.other_deductions),
    tax_regime: toInputValue(wizardData?.salary_details?.tax_regime || 'new'),
  },
  bank_details: {
    bank_name: toInputValue(wizardData?.bank_details?.bank_name || employee?.bank_name),
    bank_account_number: toInputValue(wizardData?.bank_details?.bank_account_number || employee?.bank_account_number),
    ifsc_code: toInputValue(wizardData?.bank_details?.ifsc_code || employee?.ifsc_code),
    account_holder_name: toInputValue(wizardData?.bank_details?.account_holder_name),
    branch_name: toInputValue(wizardData?.bank_details?.branch_name),
    account_type: toInputValue(wizardData?.bank_details?.account_type),
  },
  approval_hierarchy:
    Array.isArray(wizardData?.approval_hierarchy) && wizardData.approval_hierarchy.length > 0
      ? wizardData.approval_hierarchy.map((row, index) => ({
          ...createApprovalRow(index + 1),
          ...row,
          manager_id: toInputValue(row?.manager_id),
          leave_approver_id: toInputValue(row?.leave_approver_id),
          expense_approver_id: toInputValue(row?.expense_approver_id),
          other_approver_id: toInputValue(row?.other_approver_id),
        }))
      : defaultApprovalHierarchy().map((row, index) => ({
          ...row,
          manager_id: toInputValue(
            index === 0
              ? employee?.reporting_manager_id
              : index === 1
                ? employee?.second_level_manager_id
                : index === 2
                  ? employee?.third_level_manager_id
                  : employee?.fourth_level_manager_id
          ),
        })),
  live_photo_meta: {
    profile_photo_url: toInputValue(wizardData?.live_photo_meta?.profile_photo_url || employee?.profile_photo_url),
    verified_at: toInputValue(wizardData?.live_photo_meta?.verified_at),
    notes: toInputValue(wizardData?.live_photo_meta?.notes),
  },
});

const AdminEmployeeOnboardingWizardPage = ({ employeeId }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(STEP_ORDER[0]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [employee, setEmployee] = useState(null);
  const [wizardData, setWizardData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [salaryStructures, setSalaryStructures] = useState([]);
  const [form, setForm] = useState(buildInitialForm({}, {}));
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [documentUpload, setDocumentUpload] = useState({
    document_type_id: '',
    expiry_date: '',
    file: null,
  });
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const employeeOptions = useMemo(
    () =>
      employees.map((row) => ({
        value: row.employee_id,
        label: `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.employee_id,
      })),
    [employees]
  );

  const progress = Math.round(((STEP_ORDER.indexOf(activeStep) + 1) / STEP_ORDER.length) * 100);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [employeeRes, employeesRes, wizardRes, checklistRes, docTypesRes, salaryStructuresRes] = await Promise.all([
        employeeAPI.get(employeeId),
        employeeAPI.list(),
        onboardingAPI.getEmployeeOnboardingWizard(employeeId),
        onboardingAPI.getChecklists(),
        onboardingAPI.getDocumentTypes(),
        payrollAPI.getSalaryStructures(),
      ]);

      const currentEmployee = employeeRes.data;
      const wizardPayload = wizardRes.data || {};
      setEmployee(currentEmployee);
      setEmployees(filterVisibleEmployees(employeesRes.data, user));
      setWizardData(wizardPayload);
      setDocuments(wizardPayload.documents || []);
      setChecklists(checklistRes.data || []);
      setDocumentTypes(docTypesRes.data || []);
      setSalaryStructures(salaryStructuresRes.data || []);
      setForm(buildInitialForm(currentEmployee, wizardPayload));
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load onboarding wizard'));
    } finally {
      setLoading(false);
    }
  }, [employeeId, user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const stopCameraStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraOpen(false);
  }, []);

  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const updateSection = (section, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const updateListItem = (section, index, field, value) => {
    setForm((prev) => ({
      ...prev,
      [section]: prev[section].map((row, rowIndex) => (rowIndex === index ? { ...row, [field]: value } : row)),
    }));
  };

  const addListItem = (section, factory) => {
    setForm((prev) => ({
      ...prev,
      [section]: [...prev[section], factory()],
    }));
  };

  const removeListItem = (section, index) => {
    setForm((prev) => ({
      ...prev,
      [section]: prev[section].filter((_, rowIndex) => rowIndex !== index),
    }));
  };

  const buildWizardPayload = (nextStatus) => ({
    checklist_id: form.checklist_id ? Number(form.checklist_id) : null,
    hr_assigned: form.hr_assigned || null,
    buddy_assigned: form.buddy_assigned || null,
    target_days: Number(form.target_days || 30),
    status: nextStatus || form.status || 'pending',
    notes: form.notes || null,
    onboarding_profile: form.onboarding_profile,
    kyc_details: form.kyc_details,
    family_details: form.family_details,
    nominee_details: form.nominee_details,
    education_details: form.education_details,
    work_history_details: form.work_history_details,
    salary_details: {
      ...form.salary_details,
      salary_structure_id: form.salary_details.salary_structure_id ? Number(form.salary_details.salary_structure_id) : null,
      ctc_annual: form.salary_details.ctc_annual ? Number(form.salary_details.ctc_annual) : null,
    },
    bank_details: form.bank_details,
    approval_hierarchy: form.approval_hierarchy.map((row, index) => ({
      ...row,
      level: index + 1,
      manager_id: row.manager_id || null,
      leave_approver_id: row.leave_approver_id || null,
      expense_approver_id: row.expense_approver_id || null,
      other_approver_id: row.other_approver_id || null,
    })),
    live_photo_meta: form.live_photo_meta,
  });

  const persistCoreEmployeeProfile = async () => {
    await employeeAPI.update(employeeId, normalizeEmployeePayload(form));
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      await persistCoreEmployeeProfile();
      const response = await onboardingAPI.saveEmployeeOnboardingWizard(employeeId, buildWizardPayload('pending'));
      setWizardData(response.data);
      setDocuments(response.data?.documents || []);
      setForm((prev) => ({ ...prev, status: response.data?.status || 'pending' }));
      toast.success('Onboarding draft saved');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save onboarding draft'));
    } finally {
      setSaving(false);
    }
  };

  const handleSubmitOnboarding = async () => {
    setSubmitting(true);
    try {
      await persistCoreEmployeeProfile();

      if (form.salary_details.salary_structure_id && form.salary_details.ctc_annual) {
        await payrollAPI.configureEmployeeSalary(employeeId, {
          ctc_annual: Number(form.salary_details.ctc_annual),
          salary_structure_id: Number(form.salary_details.salary_structure_id),
          bank_name: form.bank_details.bank_name || null,
          bank_account_number: form.bank_details.bank_account_number || null,
          ifsc_code: form.bank_details.ifsc_code || null,
          pan_number: form.kyc_details.pan_number || null,
          tax_regime: form.salary_details.tax_regime || 'new',
        });
      }

      const response = await onboardingAPI.saveEmployeeOnboardingWizard(employeeId, buildWizardPayload('in_progress'));
      setWizardData(response.data);
      setDocuments(response.data?.documents || []);
      setForm((prev) => ({ ...prev, status: response.data?.status || 'in_progress' }));
      toast.success('Onboarding submitted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to submit onboarding'));
    } finally {
      setSubmitting(false);
    }
  };

  const uploadPhotoFile = useCallback(async (file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await employeeAPI.uploadEmployeePhoto(employeeId, formData);
      const wizardRes = await onboardingAPI.getEmployeeOnboardingWizard(employeeId);
      setWizardData(wizardRes.data);
      setForm((prev) => ({
        ...prev,
        live_photo_meta: {
          ...prev.live_photo_meta,
          profile_photo_url: wizardRes.data?.live_photo_meta?.profile_photo_url || '',
        },
      }));
      toast.success('Live photo uploaded');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload live photo'));
    } finally {
      setUploadingPhoto(false);
    }
  }, [employeeId]);

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    await uploadPhotoFile(file);
    event.target.value = '';
  };

  const startCameraCapture = useCallback(async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error('Camera is not supported in this browser');
      return;
    }
    setCameraLoading(true);
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user' },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraOpen(true);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Unable to access camera'));
      stopCameraStream();
    } finally {
      setCameraLoading(false);
    }
  }, [stopCameraStream]);

  const captureCameraPhoto = useCallback(async () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth || !video.videoHeight) {
      toast.error('Camera preview is not ready');
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      toast.error('Unable to capture photo');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });

    if (!blob) {
      toast.error('Unable to capture photo');
      return;
    }

    const capturedFile = new File([blob], `${employeeId}-live-photo.jpg`, { type: 'image/jpeg' });
    await uploadPhotoFile(capturedFile);
    stopCameraStream();
  }, [employeeId, stopCameraStream, uploadPhotoFile]);

  const handleDocumentUpload = async () => {
    if (!documentUpload.document_type_id || !documentUpload.file) {
      toast.error('Select a document type and file');
      return;
    }
    setUploadingDocument(true);
    try {
      const formData = new FormData();
      formData.append('file', documentUpload.file);
      await onboardingAPI.uploadDocument(employeeId, formData, {
        document_type_id: documentUpload.document_type_id,
        expiry_date: documentUpload.expiry_date || undefined,
      });
      const wizardRes = await onboardingAPI.getEmployeeOnboardingWizard(employeeId);
      setWizardData(wizardRes.data);
      setDocuments(wizardRes.data?.documents || []);
      setDocumentUpload({ document_type_id: '', expiry_date: '', file: null });
      toast.success('Document uploaded');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to upload document'));
    } finally {
      setUploadingDocument(false);
    }
  };

  const goToStep = (stepId) => setActiveStep(stepId);
  const goPrevious = () => {
    const currentIndex = STEP_ORDER.indexOf(activeStep);
    if (currentIndex > 0) setActiveStep(STEP_ORDER[currentIndex - 1]);
  };
  const goNext = () => {
    const currentIndex = STEP_ORDER.indexOf(activeStep);
    if (currentIndex < STEP_ORDER.length - 1) setActiveStep(STEP_ORDER[currentIndex + 1]);
  };

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <Button variant="ghost" className="mb-2 px-0" onClick={() => navigate('/admin/onboarding/employees')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back To Onboarding
          </Button>
          <h1 className="text-2xl font-bold">
            {employee?.first_name} {employee?.last_name}
          </h1>
          <p className="text-sm text-muted-foreground">
            {employee?.employee_id} • {employee?.designation || 'Employee'} • {employee?.department || 'Unassigned'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{wizardData?.status || 'pending'}</Badge>
          <Button variant="outline" onClick={handleSaveDraft} disabled={saving || submitting}>
            <Save className="mr-2 h-4 w-4" />
            {saving ? 'Saving...' : 'Save Draft'}
          </Button>
          <Button onClick={handleSubmitOnboarding} disabled={saving || submitting}>
            <CheckCircle2 className="mr-2 h-4 w-4" />
            {submitting ? 'Submitting...' : 'Submit'}
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="text-sm font-medium">Onboarding Progress</p>
              <p className="text-sm text-muted-foreground">
                Checklist: {wizardData?.checklist_name || 'Not linked'} • Documents: {documents.length}
              </p>
            </div>
            <div className="w-full max-w-sm">
              <div className="mb-2 flex items-center justify-between text-xs text-muted-foreground">
                <span>Wizard completion</span>
                <span>{progress}%</span>
              </div>
              <Progress value={progress} />
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeStep} onValueChange={goToStep}>
        <TabsList className="w-full justify-start">
          {STEP_META.map((step) => (
            <TabsTrigger key={step.id} value={step.id}>
              {step.label}
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Employee Profile</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Preferred Name</Label>
                <Input value={form.onboarding_profile.preferred_name} onChange={(e) => updateSection('onboarding_profile', 'preferred_name', e.target.value)} />
              </div>
              <div>
                <Label>Department</Label>
                <Input value={form.onboarding_profile.department} onChange={(e) => updateSection('onboarding_profile', 'department', e.target.value)} />
              </div>
              <div>
                <Label>Sub Department</Label>
                <Input value={form.onboarding_profile.sub_department} onChange={(e) => updateSection('onboarding_profile', 'sub_department', e.target.value)} />
              </div>
              <div>
                <Label>Designation</Label>
                <Input value={form.onboarding_profile.designation} onChange={(e) => updateSection('onboarding_profile', 'designation', e.target.value)} />
              </div>
              <div>
                <Label>Employment Type</Label>
                <Input value={form.onboarding_profile.employment_type} onChange={(e) => updateSection('onboarding_profile', 'employment_type', e.target.value)} />
              </div>
              <div>
                <Label>Work Mode</Label>
                <Input value={form.onboarding_profile.work_mode} onChange={(e) => updateSection('onboarding_profile', 'work_mode', e.target.value)} />
              </div>
              <div>
                <Label>Grade</Label>
                <Input value={form.onboarding_profile.grade} onChange={(e) => updateSection('onboarding_profile', 'grade', e.target.value)} />
              </div>
              <div>
                <Label>Cost Center</Label>
                <Input value={form.onboarding_profile.cost_center} onChange={(e) => updateSection('onboarding_profile', 'cost_center', e.target.value)} />
              </div>
              <div>
                <Label>Location</Label>
                <Input value={form.onboarding_profile.location} onChange={(e) => updateSection('onboarding_profile', 'location', e.target.value)} />
              </div>
              <div>
                <Label>Date Of Joining</Label>
                <Input type="date" value={form.onboarding_profile.date_of_joining} onChange={(e) => updateSection('onboarding_profile', 'date_of_joining', e.target.value)} />
              </div>
              <div>
                <Label>Probation End Date</Label>
                <Input type="date" value={form.onboarding_profile.probation_end_date} onChange={(e) => updateSection('onboarding_profile', 'probation_end_date', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>Joining Notes</Label>
                <Textarea value={form.onboarding_profile.notes} onChange={(e) => updateSection('onboarding_profile', 'notes', e.target.value)} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="kyc" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>KYC And Live Photo</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Phone</Label>
                <Input value={form.kyc_details.phone} onChange={(e) => updateSection('kyc_details', 'phone', e.target.value)} />
              </div>
              <div>
                <Label>Personal Email</Label>
                <Input value={form.kyc_details.personal_email} onChange={(e) => updateSection('kyc_details', 'personal_email', e.target.value)} />
              </div>
              <div>
                <Label>Date Of Birth</Label>
                <Input type="date" value={form.kyc_details.date_of_birth} onChange={(e) => updateSection('kyc_details', 'date_of_birth', e.target.value)} />
              </div>
              <div>
                <Label>Nationality</Label>
                <Input value={form.kyc_details.nationality} onChange={(e) => updateSection('kyc_details', 'nationality', e.target.value)} />
              </div>
              <div>
                <Label>Gender</Label>
                <Input value={form.kyc_details.gender} onChange={(e) => updateSection('kyc_details', 'gender', e.target.value)} />
              </div>
              <div>
                <Label>Marital Status</Label>
                <Input value={form.kyc_details.marital_status} onChange={(e) => updateSection('kyc_details', 'marital_status', e.target.value)} />
              </div>
              <div>
                <Label>Blood Group</Label>
                <Input value={form.kyc_details.blood_group} onChange={(e) => updateSection('kyc_details', 'blood_group', e.target.value)} />
              </div>
              <div>
                <Label>PAN Number</Label>
                <Input value={form.kyc_details.pan_number} onChange={(e) => updateSection('kyc_details', 'pan_number', e.target.value)} />
              </div>
              <div>
                <Label>Aadhaar Number</Label>
                <Input value={form.kyc_details.aadhaar_number} onChange={(e) => updateSection('kyc_details', 'aadhaar_number', e.target.value)} />
              </div>
              <div>
                <Label>UAN Number</Label>
                <Input value={form.kyc_details.uan_number} onChange={(e) => updateSection('kyc_details', 'uan_number', e.target.value)} />
              </div>
              <div>
                <Label>PF Number</Label>
                <Input value={form.kyc_details.pf_number} onChange={(e) => updateSection('kyc_details', 'pf_number', e.target.value)} />
              </div>
              <div>
                <Label>ESI Number</Label>
                <Input value={form.kyc_details.esi_number} onChange={(e) => updateSection('kyc_details', 'esi_number', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>Address</Label>
                <Textarea value={form.kyc_details.address} onChange={(e) => updateSection('kyc_details', 'address', e.target.value)} />
              </div>
              <div className="md:col-span-2">
                <Label>KYC Notes</Label>
                <Textarea value={form.kyc_details.documents_note} onChange={(e) => updateSection('kyc_details', 'documents_note', e.target.value)} />
              </div>
              <div className="md:col-span-2 rounded-lg border p-4">
                <div className="space-y-4">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-4">
                      <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-full border bg-muted">
                        {form.live_photo_meta.profile_photo_url ? (
                          <img src={form.live_photo_meta.profile_photo_url} alt="Employee live photo" className="h-full w-full object-cover" />
                        ) : (
                          <Users className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                      <div>
                        <p className="font-medium">Live Photo</p>
                        <p className="text-sm text-muted-foreground">Upload a file or capture a live photo using camera.</p>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Label className="cursor-pointer">
                        <Input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} />
                        <div className="inline-flex items-center rounded-md border px-4 py-2 text-sm font-medium">
                          <Upload className="mr-2 h-4 w-4" />
                          {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                        </div>
                      </Label>
                      {cameraOpen ? (
                        <Button type="button" variant="outline" onClick={stopCameraStream}>
                          <VideoOff className="mr-2 h-4 w-4" />
                          Stop Camera
                        </Button>
                      ) : (
                        <Button type="button" variant="outline" onClick={startCameraCapture} disabled={cameraLoading || uploadingPhoto}>
                          <Video className="mr-2 h-4 w-4" />
                          {cameraLoading ? 'Opening...' : 'Use Camera'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {cameraOpen && (
                    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
                      <div className="overflow-hidden rounded-lg border bg-black">
                        <video ref={videoRef} className="aspect-video w-full object-cover" playsInline muted autoPlay />
                      </div>
                      <div className="flex flex-col justify-between gap-3 rounded-lg border p-4">
                        <div>
                          <p className="font-medium">Camera Capture</p>
                          <p className="text-sm text-muted-foreground">Align the face inside the frame, then capture the photo.</p>
                        </div>
                        <Button type="button" onClick={captureCameraPhoto} disabled={uploadingPhoto}>
                          <Camera className="mr-2 h-4 w-4" />
                          {uploadingPhoto ? 'Saving...' : 'Capture Photo'}
                        </Button>
                      </div>
                    </div>
                  )}

                  <canvas ref={canvasRef} className="hidden" />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="family" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Family Information</CardTitle>
                <Button variant="outline" onClick={() => addListItem('family_details', createFamilyMember)}>Add Member</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {form.family_details.map((member, index) => (
                <div key={`family-${index}`} className="grid gap-4 rounded-lg border p-4 md:grid-cols-2">
                  <div>
                    <Label>Full Name</Label>
                    <Input value={member.full_name} onChange={(e) => updateListItem('family_details', index, 'full_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Input value={member.relationship} onChange={(e) => updateListItem('family_details', index, 'relationship', e.target.value)} />
                  </div>
                  <div>
                    <Label>Date Of Birth</Label>
                    <Input type="date" value={member.date_of_birth} onChange={(e) => updateListItem('family_details', index, 'date_of_birth', e.target.value)} />
                  </div>
                  <div>
                    <Label>Contact Number</Label>
                    <Input value={member.contact_number} onChange={(e) => updateListItem('family_details', index, 'contact_number', e.target.value)} />
                  </div>
                  <div>
                    <Label>Occupation</Label>
                    <Input value={member.occupation} onChange={(e) => updateListItem('family_details', index, 'occupation', e.target.value)} />
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">Dependent</p>
                      <p className="text-xs text-muted-foreground">Mark if dependent for benefits or records.</p>
                    </div>
                    <input type="checkbox" checked={member.dependent} onChange={(e) => updateListItem('family_details', index, 'dependent', e.target.checked)} />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button variant="ghost" onClick={() => removeListItem('family_details', index)} disabled={form.family_details.length === 1}>Remove</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Nominee Details</CardTitle>
                <Button variant="outline" onClick={() => addListItem('nominee_details', createNominee)}>Add Nominee</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {form.nominee_details.map((nominee, index) => (
                <div key={`nominee-${index}`} className="grid gap-4 rounded-lg border p-4 md:grid-cols-2">
                  <div>
                    <Label>Full Name</Label>
                    <Input value={nominee.full_name} onChange={(e) => updateListItem('nominee_details', index, 'full_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Relationship</Label>
                    <Input value={nominee.relationship} onChange={(e) => updateListItem('nominee_details', index, 'relationship', e.target.value)} />
                  </div>
                  <div>
                    <Label>Allocation Percentage</Label>
                    <Input value={nominee.percentage} onChange={(e) => updateListItem('nominee_details', index, 'percentage', e.target.value)} />
                  </div>
                  <div>
                    <Label>Contact Number</Label>
                    <Input value={nominee.contact_number} onChange={(e) => updateListItem('nominee_details', index, 'contact_number', e.target.value)} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Address</Label>
                    <Textarea value={nominee.address} onChange={(e) => updateListItem('nominee_details', index, 'address', e.target.value)} />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button variant="ghost" onClick={() => removeListItem('nominee_details', index)} disabled={form.nominee_details.length === 1}>Remove</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="education" className="space-y-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Education History</CardTitle>
                <Button variant="outline" onClick={() => addListItem('education_details', createEducationRow)}>Add Qualification</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {form.education_details.map((row, index) => (
                <div key={`education-${index}`} className="grid gap-4 rounded-lg border p-4 md:grid-cols-2">
                  <div>
                    <Label>Level</Label>
                    <Input value={row.level} onChange={(e) => updateListItem('education_details', index, 'level', e.target.value)} />
                  </div>
                  <div>
                    <Label>Institution</Label>
                    <Input value={row.institution_name} onChange={(e) => updateListItem('education_details', index, 'institution_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Board / University</Label>
                    <Input value={row.board_or_university} onChange={(e) => updateListItem('education_details', index, 'board_or_university', e.target.value)} />
                  </div>
                  <div>
                    <Label>Specialization</Label>
                    <Input value={row.specialization} onChange={(e) => updateListItem('education_details', index, 'specialization', e.target.value)} />
                  </div>
                  <div>
                    <Label>Graduation Year</Label>
                    <Input value={row.graduation_year} onChange={(e) => updateListItem('education_details', index, 'graduation_year', e.target.value)} />
                  </div>
                  <div>
                    <Label>Score / Percentage</Label>
                    <Input value={row.score} onChange={(e) => updateListItem('education_details', index, 'score', e.target.value)} />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button variant="ghost" onClick={() => removeListItem('education_details', index)} disabled={form.education_details.length <= 3}>Remove</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Work History</CardTitle>
                <Button variant="outline" onClick={() => addListItem('work_history_details', createWorkHistoryRow)}>Add Experience</Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {form.work_history_details.map((row, index) => (
                <div key={`work-history-${index}`} className="grid gap-4 rounded-lg border p-4 md:grid-cols-2">
                  <div>
                    <Label>Employment Type</Label>
                    <Select value={row.employment_type || 'full_time'} onValueChange={(value) => updateListItem('work_history_details', index, 'employment_type', value)}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full_time">Full Time</SelectItem>
                        <SelectItem value="part_time">Part Time</SelectItem>
                        <SelectItem value="internship">Internship</SelectItem>
                        <SelectItem value="volunteer">Volunteer</SelectItem>
                        <SelectItem value="contract">Contract</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Company Name</Label>
                    <Input value={row.company_name} onChange={(e) => updateListItem('work_history_details', index, 'company_name', e.target.value)} />
                  </div>
                  <div>
                    <Label>Job Title / Role</Label>
                    <Input value={row.job_title} onChange={(e) => updateListItem('work_history_details', index, 'job_title', e.target.value)} />
                  </div>
                  <div>
                    <Label>Location</Label>
                    <Input value={row.location} onChange={(e) => updateListItem('work_history_details', index, 'location', e.target.value)} />
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input type="date" value={row.start_date} onChange={(e) => updateListItem('work_history_details', index, 'start_date', e.target.value)} />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input type="date" value={row.end_date} onChange={(e) => updateListItem('work_history_details', index, 'end_date', e.target.value)} disabled={row.currently_active} />
                  </div>
                  <div className="md:col-span-2 flex items-center justify-between gap-3 rounded-md border px-3 py-2">
                    <div>
                      <p className="text-sm font-medium">Currently Active</p>
                      <p className="text-xs text-muted-foreground">Use this for current assignments or ongoing work.</p>
                    </div>
                    <input type="checkbox" checked={row.currently_active} onChange={(e) => updateListItem('work_history_details', index, 'currently_active', e.target.checked)} />
                  </div>
                  <div className="md:col-span-2">
                    <Label>Responsibilities</Label>
                    <Textarea value={row.responsibilities} onChange={(e) => updateListItem('work_history_details', index, 'responsibilities', e.target.value)} />
                  </div>
                  <div className="md:col-span-2 flex justify-end">
                    <Button variant="ghost" onClick={() => removeListItem('work_history_details', index)} disabled={form.work_history_details.length === 1}>Remove</Button>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="salary" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Salary Structure</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Salary Structure</Label>
                <Select value={managerSelectValue(form.salary_details.salary_structure_id)} onValueChange={(value) => updateSection('salary_details', 'salary_structure_id', value === '__none__' ? '' : value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select structure" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Not Selected</SelectItem>
                    {salaryStructures.map((structure) => (
                      <SelectItem key={structure.id} value={String(structure.id)}>{structure.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Annual CTC</Label>
                <Input type="number" value={form.salary_details.ctc_annual} onChange={(e) => updateSection('salary_details', 'ctc_annual', e.target.value)} />
              </div>
              <div>
                <Label>Monthly CTC</Label>
                <Input value={form.salary_details.ctc_monthly} onChange={(e) => updateSection('salary_details', 'ctc_monthly', e.target.value)} />
              </div>
              <div>
                <Label>Tax Regime</Label>
                <Select value={form.salary_details.tax_regime || 'new'} onValueChange={(value) => updateSection('salary_details', 'tax_regime', value)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="new">New</SelectItem>
                    <SelectItem value="old">Old</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Basic Salary</Label>
                <Input value={form.salary_details.basic_salary} onChange={(e) => updateSection('salary_details', 'basic_salary', e.target.value)} />
              </div>
              <div>
                <Label>HRA</Label>
                <Input value={form.salary_details.hra} onChange={(e) => updateSection('salary_details', 'hra', e.target.value)} />
              </div>
              <div>
                <Label>Gross Salary</Label>
                <Input value={form.salary_details.gross_salary} onChange={(e) => updateSection('salary_details', 'gross_salary', e.target.value)} />
              </div>
              <div>
                <Label>Net Salary</Label>
                <Input value={form.salary_details.net_salary} onChange={(e) => updateSection('salary_details', 'net_salary', e.target.value)} />
              </div>
              <div>
                <Label>PF Employee</Label>
                <Input value={form.salary_details.pf_employee} onChange={(e) => updateSection('salary_details', 'pf_employee', e.target.value)} />
              </div>
              <div>
                <Label>PF Employer</Label>
                <Input value={form.salary_details.pf_employer} onChange={(e) => updateSection('salary_details', 'pf_employer', e.target.value)} />
              </div>
              <div>
                <Label>ESI Employee</Label>
                <Input value={form.salary_details.esi_employee} onChange={(e) => updateSection('salary_details', 'esi_employee', e.target.value)} />
              </div>
              <div>
                <Label>ESI Employer</Label>
                <Input value={form.salary_details.esi_employer} onChange={(e) => updateSection('salary_details', 'esi_employer', e.target.value)} />
              </div>
              <div>
                <Label>Professional Tax</Label>
                <Input value={form.salary_details.professional_tax} onChange={(e) => updateSection('salary_details', 'professional_tax', e.target.value)} />
              </div>
              <div>
                <Label>Other Deductions</Label>
                <Input value={form.salary_details.other_deductions} onChange={(e) => updateSection('salary_details', 'other_deductions', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Bank Details</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-4 md:grid-cols-2">
              <div>
                <Label>Bank Name</Label>
                <Input value={form.bank_details.bank_name} onChange={(e) => updateSection('bank_details', 'bank_name', e.target.value)} />
              </div>
              <div>
                <Label>Account Holder Name</Label>
                <Input value={form.bank_details.account_holder_name} onChange={(e) => updateSection('bank_details', 'account_holder_name', e.target.value)} />
              </div>
              <div>
                <Label>Bank Account Number</Label>
                <Input value={form.bank_details.bank_account_number} onChange={(e) => updateSection('bank_details', 'bank_account_number', e.target.value)} />
              </div>
              <div>
                <Label>IFSC Code</Label>
                <Input value={form.bank_details.ifsc_code} onChange={(e) => updateSection('bank_details', 'ifsc_code', e.target.value)} />
              </div>
              <div>
                <Label>Branch Name</Label>
                <Input value={form.bank_details.branch_name} onChange={(e) => updateSection('bank_details', 'branch_name', e.target.value)} />
              </div>
              <div>
                <Label>Account Type</Label>
                <Input value={form.bank_details.account_type} onChange={(e) => updateSection('bank_details', 'account_type', e.target.value)} />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="approvals" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Assignments And Approval Hierarchy</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <Label>Checklist</Label>
                  <Select value={managerSelectValue(form.checklist_id)} onValueChange={(value) => setForm((prev) => ({ ...prev, checklist_id: value === '__none__' ? '' : value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select checklist" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not Selected</SelectItem>
                      {checklists.map((checklist) => (
                        <SelectItem key={checklist.id} value={String(checklist.id)}>{checklist.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Target Days</Label>
                  <Input type="number" value={form.target_days} onChange={(e) => setForm((prev) => ({ ...prev, target_days: e.target.value }))} />
                </div>
                <div>
                  <Label>HR Assigned</Label>
                  <Select value={managerSelectValue(form.hr_assigned)} onValueChange={(value) => setForm((prev) => ({ ...prev, hr_assigned: value === '__none__' ? '' : value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select HR owner" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not Selected</SelectItem>
                      {employeeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Buddy Assigned</Label>
                  <Select value={managerSelectValue(form.buddy_assigned)} onValueChange={(value) => setForm((prev) => ({ ...prev, buddy_assigned: value === '__none__' ? '' : value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select buddy" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not Selected</SelectItem>
                      {employeeOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="md:col-span-2">
                  <Label>Notes</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))} />
                </div>
              </div>

              <div className="space-y-4">
                {form.approval_hierarchy.map((row, index) => (
                  <div key={`approval-${index}`} className="grid gap-4 rounded-lg border p-4 md:grid-cols-2 xl:grid-cols-4">
                    <div className="xl:col-span-4 flex items-center gap-2">
                      <UserCheck className="h-4 w-4 text-primary" />
                      <span className="font-medium">{row.role_label || `Level ${index + 1}`}</span>
                    </div>
                    <div>
                      <Label>Reporting Manager</Label>
                      <Select value={managerSelectValue(row.manager_id)} onValueChange={(value) => updateListItem('approval_hierarchy', index, 'manager_id', value === '__none__' ? '' : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select manager" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Not Selected</SelectItem>
                          {employeeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Leave Approver</Label>
                      <Select value={managerSelectValue(row.leave_approver_id)} onValueChange={(value) => updateListItem('approval_hierarchy', index, 'leave_approver_id', value === '__none__' ? '' : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select approver" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Not Selected</SelectItem>
                          {employeeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Expense Approver</Label>
                      <Select value={managerSelectValue(row.expense_approver_id)} onValueChange={(value) => updateListItem('approval_hierarchy', index, 'expense_approver_id', value === '__none__' ? '' : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select approver" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Not Selected</SelectItem>
                          {employeeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Other Approver</Label>
                      <Select value={managerSelectValue(row.other_approver_id)} onValueChange={(value) => updateListItem('approval_hierarchy', index, 'other_approver_id', value === '__none__' ? '' : value)}>
                        <SelectTrigger>
                          <SelectValue placeholder="Select approver" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">Not Selected</SelectItem>
                          {employeeOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Documents</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <Label>Document Type</Label>
                  <Select value={managerSelectValue(documentUpload.document_type_id)} onValueChange={(value) => setDocumentUpload((prev) => ({ ...prev, document_type_id: value === '__none__' ? '' : value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Not Selected</SelectItem>
                      {documentTypes.map((docType) => (
                        <SelectItem key={docType.id} value={String(docType.id)}>{docType.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Expiry Date</Label>
                  <Input type="date" value={documentUpload.expiry_date} onChange={(e) => setDocumentUpload((prev) => ({ ...prev, expiry_date: e.target.value }))} />
                </div>
                <div>
                  <Label>File</Label>
                  <Input type="file" onChange={(e) => setDocumentUpload((prev) => ({ ...prev, file: e.target.files?.[0] || null }))} />
                </div>
              </div>
              <Button onClick={handleDocumentUpload} disabled={uploadingDocument}>
                <Upload className="mr-2 h-4 w-4" />
                {uploadingDocument ? 'Uploading...' : 'Upload Document'}
              </Button>

              <div className="space-y-3">
                {documents.length === 0 && (
                  <div className="rounded-lg border border-dashed p-6 text-center text-sm text-muted-foreground">
                    No documents uploaded yet.
                  </div>
                )}
                {documents.map((doc) => (
                  <div key={doc.id} className="flex items-center justify-between rounded-lg border p-3">
                    <div className="flex items-center gap-3">
                      <FileText className="h-4 w-4 text-primary" />
                      <div>
                        <p className="font-medium">{doc.document_name}</p>
                        <p className="text-xs text-muted-foreground">{doc.document_type}</p>
                      </div>
                    </div>
                    <Badge variant="outline">{doc.verification_status || 'pending'}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex items-center justify-between">
        <Button variant="outline" onClick={goPrevious} disabled={STEP_ORDER.indexOf(activeStep) === 0}>
          <ChevronLeft className="mr-2 h-4 w-4" />
          Previous
        </Button>
        <Button onClick={goNext} disabled={STEP_ORDER.indexOf(activeStep) === STEP_ORDER.length - 1}>
          Next
          <ChevronRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default AdminEmployeeOnboardingWizardPage;
